import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Settings2, Plus, Search, Pencil, Trash2, FileText, Users, Link, Hash, Activity } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  useReportesAnalytics,
  useToggleActivoAnalytics,
  useDeleteReporteAnalytics,
  useGruposDisponiblesAnalytics,
} from '../hooks/useAnalytics'
import { AnalyticsFormSheet } from '../components/AnalyticsFormSheet'
import { useAuth } from '@/auth/context/AuthContext'
import type { ReporteAnalytics } from '../types'

export function ConfiguracionAnalyticsPage() {
  const navigate = useNavigate()
  const { user, isLoading: authLoading } = useAuth()

  const isSectionAdmin = user?.is_superuser || user?.groups?.includes('admin') || user?.groups?.includes('Analytics-admin')

  useEffect(() => {
    if (!authLoading && !isSectionAdmin) {
      navigate('/', { replace: true })
    }
  }, [authLoading, isSectionAdmin, navigate])

  const [search, setSearch] = useState('')
  const [filterGrupo, setFilterGrupo] = useState<string>('all')
  const [showInactive, setShowInactive] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [editingReporte, setEditingReporte] = useState<ReporteAnalytics | null>(null)

  const { data, isLoading, error } = useReportesAnalytics({
    page: 1,
    page_size: 200,
    search: search || undefined,
    grupo_id: filterGrupo !== 'all' ? parseInt(filterGrupo) : undefined,
    activo: showInactive ? undefined : true,
  })

  const { data: grupos } = useGruposDisponiblesAnalytics()

  const toggleActivoMutation = useToggleActivoAnalytics()
  const deleteMutation = useDeleteReporteAnalytics()

  const handleToggleActivo = async (id: number) => {
    try {
      await toggleActivoMutation.mutateAsync(id)
    } catch (error) {
      console.error('Error al cambiar estado:', error)
    }
  }

  const handleDelete = async (id: number, nombre: string) => {
    if (window.confirm(`¿Estás seguro de eliminar el reporte "${nombre}"?`)) {
      try {
        await deleteMutation.mutateAsync(id)
      } catch (error) {
        console.error('Error al eliminar:', error)
      }
    }
  }

  const handleCreate = () => {
    setEditingReporte(null)
    setFormOpen(true)
  }

  const handleEdit = (reporte: ReporteAnalytics) => {
    setEditingReporte(reporte)
    setFormOpen(true)
  }

  const handleFormClose = (open: boolean) => {
    setFormOpen(open)
    if (!open) {
      setEditingReporte(null)
    }
  }

  if (authLoading || !isSectionAdmin) {
    return null
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
            <Settings2 className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold text-destructive mb-2">
              Error al cargar reportes
            </h3>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.response?.data?.error ||
                'No se pudieron cargar los reportes. Por favor, intenta nuevamente.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Settings2 className="h-6 w-6" />
          Configuración Analytics
        </h1>
        <Button onClick={handleCreate} className="bg-blue-800 hover:bg-blue-900 text-white">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Reporte
        </Button>
      </div>

      {/* Filters */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterGrupo} onValueChange={setFilterGrupo}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Grupo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los grupos</SelectItem>
                {grupos?.map((grupo) => (
                  <SelectItem key={grupo.id} value={grupo.id.toString()}>
                    {grupo.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={showInactive ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              className={showInactive ? 'bg-blue-800 hover:bg-blue-900 text-white' : ''}
            >
              {showInactive ? 'Mostrar solo activos' : 'Mostrar todos'}
            </Button>
          </div>

          {data && (
            <Badge className="badge-gradient text-xs inline-flex items-center gap-1.5 w-fit">
              <FileText className="h-3 w-3" />
              {data.count} {data.count === 1 ? 'reporte' : 'reportes'}
            </Badge>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Reportes
            {data && (
              <Badge className="bg-transparent border-[0.5px] border-blue-500 text-blue-700 dark:text-blue-400 text-xs px-2 py-0 h-5 hover:bg-transparent shadow-none">
                {data.count}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data && data.results.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead><div className="flex items-center gap-2"><Users className="h-4 w-4" />Grupo</div></TableHead>
                    <TableHead><div className="flex items-center gap-2"><FileText className="h-4 w-4" />Nombre</div></TableHead>
                    <TableHead><div className="flex items-center gap-2"><Link className="h-4 w-4" />URL</div></TableHead>
                    <TableHead><div className="flex items-center gap-2"><Hash className="h-4 w-4" />Orden</div></TableHead>
                    <TableHead><div className="flex items-center gap-2"><Activity className="h-4 w-4" />Estado</div></TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.results.map((reporte) => (
                    <TableRow key={reporte.id}>
                      <TableCell className="font-medium">{reporte.grupo_nombre}</TableCell>
                      <TableCell>{reporte.nombre}</TableCell>
                      <TableCell className="max-w-[200px]">
                        <a
                          href={reporte.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-xs truncate block"
                          title={reporte.url}
                        >
                          {reporte.url.length > 50 ? reporte.url.substring(0, 50) + '...' : reporte.url}
                        </a>
                      </TableCell>
                      <TableCell className="text-center">{reporte.orden}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={reporte.activo}
                            onCheckedChange={() => handleToggleActivo(reporte.id)}
                            disabled={toggleActivoMutation.isPending}
                            className={reporte.activo ? 'data-[state=checked]:bg-blue-500' : 'data-[state=unchecked]:bg-gray-300'}
                          />
                          <Badge className={reporte.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {reporte.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(reporte)}
                            title="Editar"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(reporte.id, reporte.nombre)}
                            disabled={deleteMutation.isPending}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 text-gray-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Settings2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No se encontraron reportes</h3>
              <p className="text-sm text-muted-foreground mb-4">
                {search || filterGrupo !== 'all'
                  ? 'No hay reportes que coincidan con los filtros.'
                  : 'No hay reportes creados aún.'}
              </p>
              <Button onClick={handleCreate} className="bg-blue-800 hover:bg-blue-900 text-white">
                <Plus className="mr-2 h-4 w-4" />
                Crear Primer Reporte
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Sheet */}
      <AnalyticsFormSheet
        open={formOpen}
        onOpenChange={handleFormClose}
        reporte={editingReporte}
      />
    </div>
  )
}
